import jsPDF from 'jspdf';
import roboto from './roboto';
import logoUdp from './logo_udp_light.png';
import logoEduSense from './edu_logo.png';
import {
    drawLink, drawHeaderImages, setRobotoFont, drawFooter, drawCoordinatesPage,
} from './drawDocumentsHelper';
import { MAT_NAME } from '../../../../../boards/matConfigs';

const drawAlphaNumericLabels = (doc, startPosition, cellWidth) => {
    doc.setTextColor('#00afff');
    [...Array(10).keys()].forEach((number) => {
        doc.text(
            String.fromCharCode(number + 65),
            startPosition[0] + (number + 1.5) * cellWidth,
            startPosition[1] + 0.88 * cellWidth,
            { align: 'center' },
        );
    });
    [...Array(10).keys()].forEach((number) => {
        doc.text(
            (number + 1).toString(),
            startPosition[0] + cellWidth - 2,
            startPosition[1] + (number + 1.5) * cellWidth + 1.5,
            { align: 'right' },
        );
    });
    doc.setTextColor('#000000');
};

const drawGrid = (doc, board, boardStart, cellWidth, imgHeight) => {
    doc.setDrawColor('#777777');

    board.forEach((row, rowIndex) => row.forEach((path, columnIndex) => {
        doc.line(
            boardStart[0] + columnIndex * cellWidth,
            boardStart[1] + rowIndex * cellWidth,
            boardStart[0] + (columnIndex + 1) * cellWidth,
            boardStart[1] + rowIndex * cellWidth,
        );
        doc.line(
            boardStart[0] + columnIndex * cellWidth,
            boardStart[1] + rowIndex * cellWidth,
            boardStart[0] + columnIndex * cellWidth,
            boardStart[1] + (rowIndex + 1) * cellWidth,
        );
        if (path.length > 0) {
            const x = boardStart[0] + 0.25 + columnIndex * cellWidth;
            const y = boardStart[1] + 0.25 + rowIndex * cellWidth;
            doc.addImage(path, 'PNG', x, y, imgHeight, imgHeight);
        }
    }));

    const cols = board[0].length;
    const rows = board.length;
    doc.line(
        boardStart[0] + cols * cellWidth,
        boardStart[1],
        boardStart[0] + cols * cellWidth,
        boardStart[1] + rows * cellWidth,
    );
    doc.line(
        boardStart[0],
        boardStart[1] + rows * cellWidth,
        boardStart[0] + cols * cellWidth,
        boardStart[1] + rows * cellWidth,
    );
};

const drawAxisLines = (doc, boardStart, cellWidth, cols, rows) => {
    doc.setDrawColor('#2773BE');
    doc.setLineWidth(1);
    doc.line(
        boardStart[0], boardStart[1] + (rows / 2) * cellWidth,
        boardStart[0] + cols * cellWidth, boardStart[1] + (rows / 2) * cellWidth,
    );
    doc.line(
        boardStart[0] + (cols / 2) * cellWidth, boardStart[1],
        boardStart[0] + (cols / 2) * cellWidth, boardStart[1] + rows * cellWidth,
    );
};

const drawCoordLabels = (doc, boardStart, cellWidth, cols, rows) => {
    const min = -(cols / 2);
    const max = cols / 2;
    const span = max - min;

    doc.setFontSize(8);
    doc.setTextColor('#2773BE');

    const centerY = boardStart[1] + (rows / 2) * cellWidth;
    const centerX = boardStart[0] + (cols / 2) * cellWidth;

    Array.from({ length: max - min - 1 }, (_, i) => min + 1 + i)
        .filter((v) => v !== 0)
        .forEach((v) => {
            const xPos = boardStart[0] + ((v - min) / span) * cols * cellWidth;
            doc.text(v.toString(), xPos, centerY + 4, { align: 'center' });
        });

    Array.from({ length: max - min - 1 }, (_, i) => min + 1 + i)
        .filter((v) => v !== 0)
        .forEach((v) => {
            const yPos = boardStart[1] + (((-v) - min) / span) * rows * cellWidth;
            doc.text(v.toString(), centerX + 2, yPos + 1);
        });

    const arrowSize = 3;
    const rightX = boardStart[0] + cols * cellWidth;
    doc.setFillColor('#2773BE');
    doc.triangle(
        rightX + arrowSize, centerY,
        rightX - 1, centerY - arrowSize,
        rightX - 1, centerY + arrowSize,
        'F',
    );

    const topY = boardStart[1];
    doc.triangle(
        centerX, topY - arrowSize,
        centerX - arrowSize, topY + 1,
        centerX + arrowSize, topY + 1,
        'F',
    );

    doc.setFontSize(12);
    doc.text('x', rightX + arrowSize + 2, centerY + 1);
    doc.text('y', centerX - 1, topY - arrowSize - 2);

    doc.setTextColor('#000000');
    doc.setFontSize(10);
};

const drawCommandsGrid = (doc, commands, commandsStart, cellWidth, imgHeight) => {
    doc.setDrawColor('#777777');
    doc.setLineWidth(0.2);
    commands.forEach((row, rowIndex) => row.forEach((path, columnIndex) => {
        doc.line(
            commandsStart[0] + columnIndex * cellWidth,
            commandsStart[1] + rowIndex * cellWidth,
            commandsStart[0] + (columnIndex + 1) * cellWidth,
            commandsStart[1] + rowIndex * cellWidth,
        );
        doc.line(
            commandsStart[0] + columnIndex * cellWidth,
            commandsStart[1] + rowIndex * cellWidth,
            commandsStart[0] + columnIndex * cellWidth,
            commandsStart[1] + (rowIndex + 1) * cellWidth,
        );
        if (path.length > 0) {
            const x = commandsStart[0] + 0.25 + columnIndex * cellWidth;
            const y = commandsStart[1] + 0.25 + rowIndex * cellWidth;
            doc.addImage(path, 'PNG', x, y, imgHeight, imgHeight);
        }
    }));

    doc.line(
        commandsStart[0] + 10 * cellWidth,
        commandsStart[1],
        commandsStart[0] + 10 * cellWidth,
        commandsStart[1] + 2 * cellWidth,
    );
    doc.line(
        commandsStart[0],
        commandsStart[1] + 2 * cellWidth,
        commandsStart[0] + 10 * cellWidth,
        commandsStart[1] + 2 * cellWidth,
    );
};

const createMatDocument = ({
    t, language, hash, link, board, commands, coordinates, activeBoard,
}) => {
    // eslint-disable-next-line
    const doc = new jsPDF();
    setRobotoFont(doc, roboto);

    drawHeaderImages(doc, logoUdp, logoEduSense);
    drawLink(doc, link, t);

    const cellWidth = 15;
    const imgHeight = 14.5;
    const isMat = activeBoard === MAT_NAME;
    const startPosition = isMat ? [16, 50] : [31, 50];
    const boardStart = isMat
        ? startPosition.map((position) => position + cellWidth)
        : [...startPosition];

    if (isMat) {
        drawAlphaNumericLabels(doc, startPosition, cellWidth);
    }

    drawGrid(doc, board, boardStart, cellWidth, imgHeight);

    const cols = board[0].length;
    const rows = board.length;
    drawAxisLines(doc, boardStart, cellWidth, cols, rows);

    if (!isMat) {
        drawCoordLabels(doc, boardStart, cellWidth, cols, rows);
    }

    if (isMat) {
        const commandsStart = [boardStart[0], boardStart[1] + 10.5 * cellWidth];
        drawCommandsGrid(doc, commands, commandsStart, cellWidth, imgHeight);
    }

    doc.setFontSize(12);
    doc.textWithLink('www.mata.uczymydzieciprogramowac.pl', 68, 265,
        { url: `https://www.mata.uczymydzieciprogramowac.pl/${language}` });
    drawFooter(doc);

    if (isMat) {
        let i = 0;
        const numberOfCoordinates = 12;
        while (i < coordinates.length / 12) {
            drawCoordinatesPage(doc, coordinates.slice(i * numberOfCoordinates, (i + 1) * numberOfCoordinates), t);
            i += 1;
        }
    }

    doc.save(`${t('wirtualna-mata')}-${!link ? `${t('zadanie')}-` : ''}${hash}.pdf`);
};

export default createMatDocument;
